import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSignUp, useUser } from '@clerk/clerk-react'
import * as Icons from '@icons'

// COMPONENTS
import Button from '@components/atoms/button'
import Spinner from '@components/atoms/spinner'
import TextField from '@components/atoms/textfield'
import { Column } from '@components/templates/column'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { useFixedSettings } from '@components/templates/fixed/_localStore/_hooks'
import { useInput } from '@hooks/inputs'
import { getErrorMessage } from '@utils/error-helper'
import { getRequiredOnboardingPath } from '@utils/user-onboarding'
import { isEmail, isNonEmptyString } from '@utils/type-checkers'


const formId = 'add-info'

const AuthAddInfoScreen: FC = () => {
    const [searchParams] = useSearchParams()
    const fromParam = searchParams.get('from')
    const hasReferralPromo = !!(fromParam && fromParam.startsWith('/referral/'))

    const { isLoaded: isUserLoaded, isSignedIn, user } = useUser()
    const { isLoaded: isSignUpLoaded, signUp, setActive } = useSignUp()
    const { createToast } = useFixedSettings()
    const navigate = useNavigate()

    const [isRedirecting, setIsRedirecting] = useState(false)
    const [firstName, firstNameProps, setFirstName] = useInput()
    const [lastName, lastNameProps, setLastName] = useInput()
    const [email, emailProps, setEmail] = useInput()

    const isContinuingOAuthSignUp = isSignUpLoaded && !isSignedIn && signUp.status === 'missing_requirements'
    const requiresEmail = isContinuingOAuthSignUp
        ? !isNonEmptyString(signUp.emailAddress)
        : !user?.emailAddresses.length
    const showEmailField = requiresEmail || isNonEmptyString(email)

    useEffect(() => {
        if (!isUserLoaded || !isSignUpLoaded) return

        const existingFirstName = user?.firstName || signUp.firstName
        const existingLastName = user?.lastName || signUp.lastName
        const existingEmail = user?.primaryEmailAddress?.emailAddress || user?.emailAddresses.at(0)?.emailAddress || signUp.emailAddress

        if (isNonEmptyString(existingFirstName) && !isNonEmptyString(firstName)) setFirstName(existingFirstName)
        if (isNonEmptyString(existingLastName) && !isNonEmptyString(lastName)) setLastName(existingLastName)
        if (isNonEmptyString(existingEmail) && !isNonEmptyString(email)) setEmail(existingEmail)
    }, [email, firstName, isSignUpLoaded, isUserLoaded, lastName, setEmail, setFirstName, setLastName, signUp.emailAddress, signUp.firstName, signUp.lastName, user])

    useEffect(() => {
        if (!isUserLoaded || !isSignUpLoaded) return

        if (!user) {
            if (isContinuingOAuthSignUp) return

            navigate(`/sign-in${document.location.search}`, { replace: true })
            return
        }

        const targetPath = getRequiredOnboardingPath(user, document.location.search)
        const currentPath = `${document.location.pathname}${document.location.search}`
        if (targetPath && targetPath !== currentPath) {
            navigate(targetPath, { replace: true })
            return
        }

        if (!targetPath) navigate(fromParam || '/sign-up/billing', { replace: true })
    }, [fromParam, isContinuingOAuthSignUp, isSignUpLoaded, isUserLoaded, navigate, user])

    const dialogRef = useRef<HTMLDialogElement>(null)
    useEffect(() => {
        if (!dialogRef.current) return

        if (isRedirecting) dialogRef.current.showModal()
        else dialogRef.current.close()
    }, [isRedirecting])

    const submittable = useMemo(() => {
        const hasName = isNonEmptyString(firstName) && isNonEmptyString(lastName)
        const hasValidEmail = !requiresEmail || (isNonEmptyString(email) && isEmail(email))
        return hasName && hasValidEmail
    }, [email, firstName, lastName, requiresEmail])

    async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        try {
            event.preventDefault()

            if (!isUserLoaded || !isSignUpLoaded) throw new Error('Auth Services have yet to load.')

            if (isContinuingOAuthSignUp) {
                if (setActive == null) throw new Error('Auth Services have yet to load.')

                setIsRedirecting(true)
                const signUpResult = await signUp.update({
                    firstName,
                    lastName,
                    ...(requiresEmail && isNonEmptyString(email) && isEmail(email) ? { emailAddress: email } : {})
                })

                if (signUpResult.status !== 'complete' || !isNonEmptyString(signUpResult.createdSessionId)) {
                    const missingFields = signUpResult.missingFields.join(', ')
                    throw new Error(missingFields ? `Unable to complete sign up. Missing: ${missingFields}` : 'Unable to complete sign up.')
                }

                await setActive({ session: signUpResult.createdSessionId })
                navigate(fromParam || '/sign-up/billing', { replace: true })
                return
            }

            if (user?.update == null) throw new Error('Auth Services have yet to load.')

            await user.update({ firstName, lastName })

            if (requiresEmail && isNonEmptyString(email) && isEmail(email)) {
                try {
                    const emailResult = await user.createEmailAddress({ email })
                    const baseUrl = import.meta.env.VITE_VIRTUAL_CARD_BASE_URL.replace('.api', '').replace('api', '')
                    const redirectUrl = `${baseUrl}/account/profile/email/${emailResult.id}/verify`

                    await emailResult.prepareVerification({
                        strategy: 'email_link',
                        redirectUrl
                    })
                } catch (error) {
                    console.log('email error:', error)
                }
            }

            setIsRedirecting(true)
            setTimeout(async () => {
                try {
                    await user.reload()
                } catch (error) {
                    console.error('Failed to reload user:', error)
                }

                const nextPath = getRequiredOnboardingPath(window.Clerk?.user, document.location.search)
                if (nextPath) navigate(nextPath, { replace: true })
                else navigate(fromParam || '/sign-up/billing', { replace: true })
            }, 1000)
        } catch (error: unknown) {
            setIsRedirecting(false)
            console.log(error)
            createToast({
                type: 'error',
                text: getErrorMessage(error, 'An issue occured while trying to update your user profile.')
            })
        }
    }

    return (<>
        <Column.Content style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                <h1 className='text-center text-blue-500'>Welcome!</h1>
                <h2 className='text-center'>Create Your Account</h2>
                {hasReferralPromo && (
                    <div style={{
                        background: '#ecfdf5',
                        border: '1px solid #10b981',
                        borderRadius: '1rem',
                        padding: '1rem',
                        marginTop: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem'
                    }}>
                        <div style={{
                            animation: 'bounce 1s ease-out 2',
                            animationDelay: '0.3s',
                            background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                            borderRadius: '0.75rem',
                            height: '3rem',
                            width: '3rem',
                            minWidth: '3rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                        }}>
                            <Icons.GiftBox width="1.5rem" height="1.5rem" tint="white" />
                        </div>
                        <div>
                            <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.9375rem', fontWeight: '600', color: '#064e3b' }}>Referral Promotion</h4>
                            <p style={{ margin: 0, fontSize: '0.8125rem', lineHeight: '1.4', color: '#065f46' }}>
                                You are eligible for the referral. Your credits will be applied once the steps are completed.
                            </p>
                        </div>
                    </div>
                )}
            </div>
            <div style={{ flex: 1 }} />
            <form id={formId} onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
                <TextField
                    {...firstNameProps}
                    name='given-name'
                    placeholder='First Name'
                    autoComplete='given-name'
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            const lastNameInput = document.querySelector('input[name="family-name"]') as HTMLInputElement
                            lastNameInput?.focus()
                        }
                    }}
                />
                <TextField
                    {...lastNameProps}
                    name='family-name'
                    placeholder='Last Name'
                    autoComplete='family-name'
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            const nextField = document.querySelector('input[name="email"]') as HTMLInputElement
                            nextField?.focus()
                        }
                    }}
                />
                {showEmailField && (
                    <TextField
                        {...emailProps}
                        type='email'
                        name='email'
                        placeholder='E-mail Address'
                        autoComplete='email'
                        readOnly={!requiresEmail}
                        required={requiresEmail}
                    />
                )}
            </form>
            <div style={{ flex: 2 }} />
        </Column.Content>
        <Column.Footer>
            <Button form={formId} type='submit' disabled={!submittable}>
                Continue
            </Button>
        </Column.Footer>
        <dialog ref={dialogRef}>
            <div>
                <div className='flex flex-col items-center justify-center' style={{ background: 'var(--splitcard-white)', gap: '1rem', padding: '1rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                    <Spinner />
                </div>
            </div>
        </dialog>
    </>)
}

const AddInfoRoute: RouteObject = {
    path: 'add-info',
    element: <AuthAddInfoScreen />,
}

export default AddInfoRoute
